import { ApiError } from './apiService';
import { normalizeText, toNonEmptyText } from '../utils';

interface ApiValidationError {
  field?: string;
  message: string;
}

interface ParsedApiErrorData {
  title?: string;
  detail?: string;
  message?: string;
  errors: ApiValidationError[];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isNetworkErrorMessage(message: string): boolean {
  const normalizedMessage = normalizeText(message);

  return (
    normalizedMessage === 'failed to fetch' ||
    normalizedMessage === 'load failed' ||
    normalizedMessage.includes('networkerror') ||
    normalizedMessage.includes('network request failed') ||
    normalizedMessage.includes('network error')
  );
}

function parseApiErrorData(error: unknown): ParsedApiErrorData | undefined {
  if (!(error instanceof ApiError)) return undefined;
  if (!isRecord(error.data)) return undefined;

  const errorsRaw = error.data.errors;
  const validationErrors: ApiValidationError[] = [];

  if (Array.isArray(errorsRaw)) {
    errorsRaw.forEach(errorItem => {
      if (!isRecord(errorItem)) return;

      const field = toNonEmptyText(errorItem.field);
      const message = toNonEmptyText(errorItem.message);
      if (!message) return;

      validationErrors.push({ field, message });
    });
  }

  return {
    title: toNonEmptyText(error.data.title),
    detail: toNonEmptyText(error.data.detail),
    message: toNonEmptyText(error.data.message),
    errors: validationErrors,
  };
}

export function getApiErrorMessage(error: unknown, fallback: string): string {
  const parsedErrorData = parseApiErrorData(error);
  const firstValidationError = parsedErrorData?.errors[0]?.message;
  if (firstValidationError) return firstValidationError;

  if (parsedErrorData?.detail) return parsedErrorData.detail;
  if (parsedErrorData?.title) return parsedErrorData.title;
  if (parsedErrorData?.message) return parsedErrorData.message;

  if (error instanceof Error) {
    const message = toNonEmptyText(error.message);
    if (message) {
      if (isNetworkErrorMessage(message)) return fallback;
      return message;
    }
  }

  return fallback;
}

export function getApiFieldErrorMessage(
  error: unknown,
  fieldName: string,
): string | undefined {
  const parsedErrorData = parseApiErrorData(error);
  if (!parsedErrorData) return undefined;

  const fieldError = parsedErrorData.errors.find(
    validationError => validationError.field === fieldName,
  );

  return fieldError?.message;
}
